import React, { useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';
import { SubWindowsHost } from '../../global/sub-window/sub-windows-host';
import { withViewModel } from '../../../infrastructure/components/with-view-model';
import { Paging } from '../../global/_paging/paging-container';
import { connectEventsVM } from '../events.context';
import { IEventDetailsDTO, TGetEventsSortKeys } from '../../../api/events.dao';
import { ITheadSort, ListLayout } from '../../global/tables/table-list-layout';
import DefaultButton from '../../global/default-button/default-button';
import { SearchInput } from '../../global/search-input/search-input';
import TableLoadingRow from '../../global/tables/table-loading-row';
import Text from '../../global/text/text';
import { Permission } from '../../../api/roles.dao';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { getNavigationLabels, SEPARATOR } from '../../../constants/navigation-labels.constants';
import MultiSelectionDropdown from '../../global/multi-selection-dropdown/multi-selection-dropdown';
import { EventsListViewModel } from './events-list.viewmodel';
import { EventListItem } from './_event-list-item/event-list-item';
import { DATE_TEXT, EMPTY_SEARCH_RESULT, EXTERNAL_ID_TEXT, MORE_BUTTON_TEXT, TYPE } from '../../../constants/common-labels.constants';
import { createBookEventText, createEventText } from '../../../constants/EventValues';
import OptionsButton from '../../global/options-button/options-button';

const EventsListPresentation = injectIntl(mobxReact.observer((props: {vm: EventsListViewModel; intl: IntlShape}) => {
    const { vm, intl } = props;

    const labels = getNavigationLabels(intl);
    const createPermission = hasPermission(Permission.CREATE_NEW_EVENT);
    const hasBookingPermission = hasPermission(Permission.BOOK_AN_EVENT) && vm.orderBookingEnabled;
    const accountName = vm.getAccountName();
    const eventsList = vm.getEventsList();
    const title = `${accountName} Events`;

    const isEmpty = !eventsList?.length && !vm.getIsLoading();

    const deliveryTeamNoItems = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.delivery.emptyList'
                    defaultMessage="You don't have any Delivery Team members yet."
                    description='No Delivery Team label text'
                />
            </div>
        </div>
    );

    const deliveryTeamNoSearchResult = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.delivery.notFound'
                    defaultMessage='No Delivery Team members found.'
                    description='No Delivery Team label text'
                />
            </div>
        </div>
    );

    const noCoordinatorsFilterPlaceholder = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.coordinators.emptyList'
                    defaultMessage="You don't have any coordinators yet."
                    description='No coordinators label text'
                />
            </div>
        </div>
    );

    const coordinatorsNoSearchResult = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.coordinators.notFound'
                    defaultMessage='No coordinators found.'
                    description='No coordinators label text'
                />
            </div>
        </div>
    );

    const theads: ITheadSort<TGetEventsSortKeys>[] = [
        {
            title: intl.formatMessage({
                id: `oe.common.name`,
                description: 'Label text',
                defaultMessage: 'Name',
            }),
            sortField: 'name',
            width: '10%'
        },
        {
            title: intl.formatMessage(DATE_TEXT),
            sortField: 'startDateTime',
            width: '30%'
        },
        {
            title: intl.formatMessage(TYPE),
            sortField: 'type',
            width: '10%'
        },
        {
            title: intl.formatMessage({
                id: `oe.common.blueprint`,
                description: 'Label text',
                defaultMessage: 'Blueprint',
            }),
            width: '15%'
        },
        {
            title: intl.formatMessage({
                id: `oe.common.OENumber`,
                description: 'Label text',
                defaultMessage: 'OE Number',
            }),
            width: '10%'
        },
        {
            title: intl.formatMessage(EXTERNAL_ID_TEXT),
            width: '10%'
        },
        {
            title: <MultiSelectionDropdown
                entityName={intl.formatMessage({
                    id: `oe.common.coordinators`,
                    description: 'Label text',
                    defaultMessage: 'Coordinators',
                })}
                noItemsPlaceholder={noCoordinatorsFilterPlaceholder}
                onFetchItems={vm.fetchAccountCoordinatorsList}
                onApply={coordinatorsIds => vm.handleChangeCoordinatorsFilter(coordinatorsIds)}
                withBESearch={true}
                noSearchResultText={coordinatorsNoSearchResult}
            />,
            width: '10%'
        },
        {
            title: <MultiSelectionDropdown
                entityName={intl.formatMessage({
                    id: `oe.delivery.team`,
                    description: 'Label text',
                    defaultMessage: 'Delivery Team'
                })}
                noItemsPlaceholder={deliveryTeamNoItems}
                onApply={deliveryTeamIds => vm.handleChangeDeliveryTeamFilter(deliveryTeamIds)}
                onFetchItems={vm.fetchFetchDeliveryTeamList}
                withBESearch={true}
                noSearchResultText={deliveryTeamNoSearchResult}
            />,
            width: '10%'
        },
        vm.anonymizationEnabled &&
        {
            title: intl.formatMessage({
                id: `oe.accountAdmin.anonymized`,
                description: 'Label text',
                defaultMessage: 'Anonymized',
            }),
            width: '15%'
        },
        vm.anonymizationEnabled &&
        {
            title: intl.formatMessage({
                    id: `oe.accountAdmin.dateOfAnonymization`,
                    description: 'Label text',
                    defaultMessage: 'Date of anonymization',
                }),
                width: '20%'
        },
        {width: '8%'},
        {width: '8%'},
        {width: '8%'},
        { width: '145px', customNode:
                <div className={classNames('search-input-container', {
                    'no-result': (
                        !vm.getEventsList()?.length &&
                        !!vm.searchQuery
                    )
                })}>
                    <SearchInput
                        resetSearch={() => vm.clearSearchQuery()}
                        onChange={e => vm.setSearchQuery(e.target.value)}
                        withResetSearchButton
                    />
                </div>
        },
    ];

    const isInternalEventLinksPermission = hasPermission(Permission.VIEW_INTERNAL_EVENT_LINKS);

    useEffect(() => {
        if (accountName) {
            document.title = `${labels.ROOT} ${SEPARATOR} ${accountName} ${labels.EVENTS_LIST}`;
        }
    }, [accountName]);

    const meetingsOptions = [
        hasBookingPermission && {
            label: intl.formatMessage(createBookEventText, { prefix: '' }),
            action: vm.onNewBookingClick,
        },
    ].filter(Boolean);

    return (
        <SubWindowsHost>
            <div className='page-container'>
                <section className='events-title horizontal-row horizontal-row--with-bottom-margin'>
                    <Text message={title} tag='h1' withEllipsis />
                    { (createPermission || hasBookingPermission) &&
                    <div className='horizontal-row__align-to-row-end'>
                        <div className='buttons-block'>
                            { createPermission && (
                                <DefaultButton
                                    modifier={['fat', 'confirm-safe']}
                                    text={intl.formatMessage(createEventText, { prefix: '+ ' })}
                                    onClick={vm.createEvent}
                                />
                            )}
                            { !createPermission && hasBookingPermission && (
                                <DefaultButton
                                        modifier={['fat', 'confirm-safe']}
                                        text={intl.formatMessage(createBookEventText, { prefix: '+ ' })}
                                        onClick={vm.onNewBookingClick}
                                />
                            )}
                            { createPermission && hasBookingPermission &&
                                <OptionsButton
                                    modifier={['fat', 'options']}
                                    label={intl.formatMessage(MORE_BUTTON_TEXT)}
                                    options={meetingsOptions}
                                />
                            }
                        </div>
                    </div>
                    }
                </section>
                <section className='events-list fixed-list-layout'>
                    <div>
                        <ListLayout
                            sortOptions={{
                                theads: theads,
                                sortRows: vm.setSortParams,
                                sortOrder: vm.getSortOrder(),
                                sortField: vm.getSortField()
                            }}
                            isDisabled={isEmpty}
                        >
                            {vm.getIsLoading() && <TableLoadingRow colSpan={theads.length} />}
                            {!vm.getIsLoading() && eventsList.map((item: IEventDetailsDTO) => {
                            return <EventListItem
                                accountId={vm.getAccountId()}
                                event={item}
                                key={item.eventId}
                                startEditEvent={() => vm.editEvent(item.eventId)}
                                isSelected={vm.isEventEditFormShown(item.eventId)}
                                searchQuery={vm.searchQuery}
                                navigateToEventRoute={vm.navigateToEventRoute}
                                isInternalEventLinksPermission={isInternalEventLinksPermission}
                                anonymizationEnabled={vm.anonymizationEnabled}
                                eventBlueprintName={vm.getBlueprintNameById(item.blueprintId)}
                            />;
                            })
                        }
                        </ListLayout>
                        {isEmpty &&
                            <div className='empty-list-message'>
                                {vm.selectedFilterDeliveryTeam || vm.searchQuery ?
                                    <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                                    :
                                    <FormattedMessage
                                        description='Event list empty text'
                                        defaultMessage='No events have been scheduled in this account.'
                                        id='oe.events.emptyListText'
                                    />
                                }
                            </div>
                        }
                    </div>
                    <Paging pagingVM={vm.getPagingVM()} />
                </section>
            </div>
        </SubWindowsHost>
    );
}));

interface IContainerProps extends IEventsListProps{
    intl: IntlShape;
}

interface IEventsListProps {
    accountId: number;
    organizationName: string;
    anonymizationEnabled: boolean;
    orderBookingEnabled: boolean;
}

const EventsListContainer = injectIntl(withViewModel(
    EventsListPresentation,
    (props: IContainerProps) => {
        const viewModel = new EventsListViewModel(props.accountId, props.organizationName, props.anonymizationEnabled, props.orderBookingEnabled, props.intl);
        viewModel.initialize();
        return viewModel;
    },
    props => [props.accountId, props.organizationName, props.anonymizationEnabled, props.orderBookingEnabled, props.intl],
));

const EventsList = connectEventsVM<IEventsListProps>(EventsListContainer, vm => {
    return {
        accountId: vm.getAccountId(),
        organizationName: vm.getAccount()?.name ?? '',
        anonymizationEnabled: vm.getAccount()?.anonymizationEnabled,
        orderBookingEnabled: vm.getAccount()?.orderBookingEnabled ?? false,
    };
});

export { EventsList, EventsListPresentation };
